import { Menu, Transition } from '@headlessui/react';
import {
  ArrowRightOnRectangleIcon,
  CreditCardIcon,
  LanguageIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';
import { UserCircleIcon } from '@heroicons/react/24/solid';
import { User } from '@supabase/supabase-js';
import { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { UpgradeModal } from '@/common/components/UpgradeModal';
import { supabase } from '@/common/config/supabase';
import { useSubscriptionStore } from '@/common/stores/useSubscriptionStore';

interface UserMenuProps {
  user: User | null;
}

export const UserMenu = ({ user }: UserMenuProps) => {
  const { t, i18n } = useTranslation();
  const { isPremium, stripeCustomerId, createPortalSession } = useSubscriptionStore();
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  const handleLogout = async () => {
    await supabase.auth.signOut();
  };

  const handleManageSubscription = async () => {
    const url = await createPortalSession();
    if (url) {
      window.location.href = url;
    }
  };

  const displayName = user?.user_metadata.name || user?.email?.split('@')[0] || 'User';
  const avatarUrl = user?.user_metadata.avatar_url;

  return (
    <>
      <Menu as="div" className="relative">
        <Menu.Button className="flex items-center gap-3 hover:opacity-80">
          <div className="text-right">
            <div className="flex items-center gap-1.5 justify-end">
              <h3 className="text-sm font-medium">{displayName}</h3>
              {isPremium && (
                <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-gray-200 text-gray-900">
                  <SparklesIcon className="h-2.5 w-2.5" />
                  PRO
                </span>
              )}
            </div>
            <p className="text-xs text-gray-500">{user?.email}</p>
          </div>
          {avatarUrl ? (
            <img alt="User Avatar" className="h-8 w-8 rounded-full" src={avatarUrl} />
          ) : (
            <UserCircleIcon className="h-8 w-8 text-gray-400" />
          )}
        </Menu.Button>

        <Transition
          as={Fragment}
          enter="transition ease-out duration-100"
          enterFrom="transform opacity-0 scale-95"
          enterTo="transform opacity-100 scale-100"
          leave="transition ease-in duration-75"
          leaveFrom="transform opacity-100 scale-100"
          leaveTo="transform opacity-0 scale-95"
        >
          <Menu.Items className="absolute right-0 mt-2 w-60 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
            <div className="py-1">
              {isPremium && stripeCustomerId ? (
                <Menu.Item>
                  {({ active }) => (
                    <button
                      className={`${
                        active ? 'bg-gray-100' : ''
                      } w-full text-left px-4 py-2 text-sm text-gray-700 flex items-center gap-2`}
                      onClick={handleManageSubscription}
                    >
                      <CreditCardIcon className="h-4 w-4" />
                      {t('userMenu.manageSubscription')}
                    </button>
                  )}
                </Menu.Item>
              ) : (
                <Menu.Item>
                  {({ active }) => (
                    <button
                      className={`${
                        active ? 'bg-gray-100' : ''
                      } w-full text-left px-4 py-2 text-sm text-gray-900 font-medium flex items-center gap-2`}
                      onClick={() => setShowUpgradeModal(true)}
                    >
                      <SparklesIcon className="h-4 w-4" />
                      {t('userMenu.upgradeToPremium')}
                    </button>
                  )}
                </Menu.Item>
              )}
              <div className="border-t border-gray-100 my-1" />
              <div className="px-4 py-2 flex items-center justify-between">
                <div className="flex items-center gap-2 text-sm text-gray-700">
                  <LanguageIcon className="h-4 w-4" />
                  {t('userMenu.language')}
                </div>
                <div className="flex items-center gap-1 rounded-lg border border-gray-200 bg-gray-50 p-0.5">
                  <button
                    className={`rounded-md px-2.5 py-1 text-xs font-medium transition-colors ${
                      i18n.language.startsWith('en') ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'
                    }`}
                    type="button"
                    onClick={() => i18n.changeLanguage('en')}
                  >
                    EN
                  </button>
                  <button
                    className={`rounded-md px-2.5 py-1 text-xs font-medium transition-colors ${
                      i18n.language.startsWith('pt') ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'
                    }`}
                    type="button"
                    onClick={() => i18n.changeLanguage('pt')}
                  >
                    PT
                  </button>
                </div>
              </div>
              <div className="border-t border-gray-100 my-1" />
              <Menu.Item>
                {({ active }) => (
                  <button
                    className={`${
                      active ? 'bg-gray-100' : ''
                    } w-full text-left px-4 py-2 text-sm text-gray-700 flex items-center gap-2`}
                    onClick={handleLogout}
                  >
                    <ArrowRightOnRectangleIcon className="h-4 w-4" />
                    {t('userMenu.logout')}
                  </button>
                )}
              </Menu.Item>
            </div>
          </Menu.Items>
        </Transition>
      </Menu>

      <UpgradeModal isOpen={showUpgradeModal} onClose={() => setShowUpgradeModal(false)} />
    </>
  );
};
